/**
 * Org baseline bot-defaults card (Org Settings, admin). Holds the FULL config
 * for each kind — including the strategy (fill-avoidance) knobs traders don't
 * tune. Self-fetches GET /api/manager/org, saves via PATCH /api/manager/org
 * { botDefaults }, then refreshMe() so the acting user's session picks it up.
 */
import { useEffect, useState } from 'react';
import { Bot, Loader2, ToggleLeft, ToggleRight } from 'lucide-react';
import { apiFetch, useAuthStore } from '../../stores/authStore';
import type { BotDefaults, BotKindConfig } from '../../lib/bots/botDefaults';
import { BotDefaultsEditor, cloneByKind } from '../../components/bots/BotDefaultsEditor';

type ByKind = Partial<Record<string, BotKindConfig>>;

export function OrgBotDefaultsPanel({
  orgQueryParam,
  className = 'mb-6',
  onEnabledChange,
}: {
  orgQueryParam: string;
  className?: string;
  /** Reports the org's bot switch up so the parent can gate the per-trader
   *  toggles in lockstep (effective access = org AND user). */
  onEnabledChange?: (v: boolean) => void;
}) {
  const refreshMe = useAuthStore((s) => s.refreshMe);
  const [stored, setStored] = useState<BotDefaults | null>(null);
  const [byKind, setByKind] = useState<ByKind>({});
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);
  // A rejected field never reaches `byKind`, so Save would otherwise stay
  // enabled on the strength of an earlier valid edit and quietly save something
  // other than what the admin is looking at.
  const [editorValid, setEditorValid] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setDirty(false);
    setEnabled(null);
    (async () => {
      try {
        const res = await apiFetch(`/api/manager/org${orgQueryParam}`);
        const data = await res.json();
        if (cancelled) return;
        const bd = (res.ok ? data.org?.botDefaults : null) as BotDefaults | null;
        setStored(bd ?? null);
        setByKind(cloneByKind(bd?.byKind));
        setEnabled(res.ok ? !!data.org?.botsEnabled : false);
        setLoaded(true);
      } catch {
        if (!cancelled) {
          setStored(null);
          setByKind({});
          setEnabled(false);
          setLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgQueryParam]);

  // Report the resolved org switch to the parent so it can gate the per-trader
  // bot toggles in lockstep (incl. live, when toggled here).
  useEffect(() => {
    if (enabled !== null) onEnabledChange?.(enabled);
  }, [enabled, onEnabledChange]);

  const toggleEnabled = async () => {
    if (enabled === null || toggling) return;
    const next = !enabled;
    setToggling(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/manager/org${orgQueryParam}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ botsEnabled: next }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? 'Update failed');
        return;
      }
      setEnabled(!!data.org?.botsEnabled);
      void refreshMe(); // flip botAccess live in the acting session
    } catch {
      setError('Update failed');
    } finally {
      setToggling(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const body: BotDefaults = { ...(stored ?? {}), byKind };
      const res = await apiFetch(`/api/manager/org${orgQueryParam}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ botDefaults: body }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? 'Save failed');
        return;
      }
      setStored((data.org?.botDefaults ?? null) as BotDefaults | null);
      setDirty(false);
      setSaved(true);
      void refreshMe();
    } catch {
      setError('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={`bg-card border-border rounded-lg border p-4 ${className}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Bot size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-semibold">Trading Bots</h3>
        </div>
        <button
          onClick={toggleEnabled}
          disabled={enabled === null || toggling}
          title={enabled ? 'Bots enabled for this org — click to disable' : 'Bots disabled — click to enable'}
          className="text-muted-foreground hover:text-foreground flex shrink-0 items-center gap-1.5 text-xs font-medium disabled:opacity-50"
        >
          {toggling || enabled === null ? (
            <Loader2 size={16} className="animate-spin" />
          ) : enabled ? (
            <ToggleRight size={18} className="text-emerald-500" />
          ) : (
            <ToggleLeft size={18} className="text-muted-foreground" />
          )}
          <span>{enabled === null ? '' : enabled ? 'Enabled' : 'Disabled'}</span>
        </button>
      </div>
      <p className="text-muted-foreground mt-1 text-[11px]">
        Let this org's bot-access traders deploy + claim market-making bots from the
        Value Dashboard (each trader also needs the per-member toggle). Bots run on
        the org's edge engine + keys. When enabled, set the per-kind baseline below —
        blank inherits the engine built-in; traders can override the basic fields per
        spawn.
      </p>

      {!loaded ? (
        <div className="text-muted-foreground mt-3 text-xs">Loading…</div>
      ) : enabled ? (
        <div className="mt-4">
          <BotDefaultsEditor
            byKind={byKind}
            scope="org"
            onValidityChange={setEditorValid}
            onChange={(next) => {
              setByKind(next);
              setDirty(true);
              setSaved(false);
            }}
          />
          <div className="mt-3 flex items-center gap-3">
            <button
              onClick={save}
              disabled={saving || !dirty || !editorValid}
              className="bg-primary text-primary-foreground rounded px-3 py-1.5 text-xs font-medium disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save defaults'}
            </button>
            {saved && !dirty && <span className="text-xs text-emerald-500">Saved</span>}
          </div>
        </div>
      ) : null}
      {error && <div className="mt-2 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</div>}
    </section>
  );
}
